import queryString from 'query-string';
import { feverApiFetchOpts, fetches, methods } from 'api/commom';
import { getWebsiteAPI } from 'api/zines';
import permissionsFunctionMapping, {
    permissionsRouteMapping
} from 'config/permissionsFunctionMapping';
import { CREATE_GIDP_ACCOUNT_ERROR, UPDATE_GIDP_ACCOUNT_ERROR } from 'apiHandler/sponsor';
import * as errorTypes from 'config/errorTypes';
import {
    isRoleAuthoritiesFailedError,
    isRoleNoPermissionError,
    isOtpValidationFailedError,
    isSponsorNotExistError
} from 'utils/errorObjectChecker';

export function getSponsorListApi() {
    return fetches(`/services/sponsors`, feverApiFetchOpts());
}

export function getUserSponsors() {
    return getSponsorListApi()
        .then(sponsors => (Array.isArray(sponsors) ? sponsors : null))
        .catch(err => {
            if (err.httpStatus === 404) {
                return null;
            } else {
                throw err;
            }
        });
}

export async function getSponsor(sponsorId) {
    try {
        const sponsorData = await getSponsorApi(sponsorId);

        const [
            sponsorSettingData,
            accountRole,
            orders = [],
            availablePlanItems,
            zinesInfo
        ] = await Promise.all([
            getSponsorSettingsApi(sponsorId),
            getAccountRoleInfo(sponsorId),
            getSponsorOrder(sponsorId),
            getSponsorAvailPlanItems(sponsorId),
            getWebsiteAPI(sponsorId)
        ]);
        const existOrder = orders.filter(order => !order.deletedAt);
        const { username, isCustomDomain, domain, bindZines } = zinesInfo;
        return {
            sponsor: {
                ...sponsorData,
                thirdParty: {},
                settings: { ...sponsorSettingData },
                accountRole: {
                    ...accountRole,
                    permissionsFunction: permissionsFunctionMapping(accountRole.permissions),
                    permissionsRoute: permissionsRouteMapping(accountRole.permissions)
                },
                plans: existOrder,
                zines: {
                    username,
                    isCustomDomain,
                    domain,
                    bindZines
                }
            },
            availablePlanItems
        };
    } catch (err) {
        // 403 帶出錯誤類型讓呼叫端區分「查無角色（不屬於此 sponsor）」「角色權限不足」
        // 「OTP 未驗證」—— 原本壓成裸的數字 403 會讓這三種情境無法分辨，
        // 導致 OTP 未驗證的使用者被導到無權限頁而非 OTP 驗證頁
        if (err.httpStatus === 403) {
            return {
                httpStatus: 403,
                isRoleAuthoritiesFailed: isRoleAuthoritiesFailedError(err),
                isRoleNoPermission: isRoleNoPermissionError(err),
                isOtpValidationFailed: isOtpValidationFailedError(err)
            };
        }

        // sponsorId 不存在（400 SP-R1-FNF1）：不是權限問題，也不能壓成 null ——
        // 壓成 null 呼叫端只會拿別的 sponsorId 再打一次，使用者最後看到的是
        // 無 reason 的「無使用權限」，看不出真正原因是連結上的編號不存在
        if (isSponsorNotExistError(err)) {
            return { httpStatus: err.httpStatus, isSponsorNotExist: true };
        }

        if (err.httpStatus >= 400) {
            return null;
        } else {
            throw err;
        }
    }
}

export function getSponsorApi(sponsorId) {
    return fetches(`/services/sponsors/${sponsorId}`, feverApiFetchOpts());
}

export function getSponsorSettingsApi(sponsorId) {
    return fetches(`/services/sponsors/${sponsorId}/settings`, feverApiFetchOpts());
}

export function createSponsorApi({ name }) {
    return fetches(
        `/services/sponsors`,
        feverApiFetchOpts({ method: methods.POST, body: { name } })
    );
}

export function createSponsorByFansPageApi({ sponsorId = '', facebookFanPageId, name }) {
    return fetches(
        `/services/sponsors/fans`,
        feverApiFetchOpts({
            method: methods.POST,
            body: { facebookFanPageId, name, sponsorId, facebookFanPageName: name }
        })
    );
}

export function getSponsorOrder(sponsorId) {
    return fetches(`/services/sponsors/${sponsorId}/plans/orders`, feverApiFetchOpts());
}

export function updateSponsorApi(sponsorId, data) {
    return fetches(
        `/services/sponsors/${sponsorId}`,
        feverApiFetchOpts({ method: methods.PATCH, body: data })
    );
}

/**
 * 設定主辦單位自訂網域 (CNAME) — promotion-tool
 * Spec: prompt-library/backend/Api/promotion-tool/設定主辦單位自訂網域.yaml
 * ClickUp: https://app.clickup.com/t/86ex8g6re
 *
 * 使用 sponsor UUID（非 numeric id）。後端僅允許設定一次，且 DNS CNAME record
 * 須先設定，否則 ValidCname 規則會擋下。
 */
export function updateSponsorCnameApi(sponsorUuid, customDomain) {
    // 後端驗證失敗訊息實際存於 data.customDomain[]，payload.message 為 Laravel 通用文案
    // （實測為 "validation failed"，不可拿來判斷子分類）
    const matchCustomDomainMessage = (r, regex) => {
        const messages = r?.payload?.data?.customDomain;
        return Array.isArray(messages) && messages.some(m => regex.test(m));
    };
    return fetches(
        `/services/sponsors/${sponsorUuid}/cname`,
        feverApiFetchOpts({ method: methods.PATCH, body: { customDomain } }),
        {
            throwErrConfigs: {
                [errorTypes.SPONSOR_CNAME_ALREADY_SET]: r =>
                    r.httpStatus === 422 && r?.payload?.code === 'SP-V0-F0',
                [errorTypes.SPONSOR_CNAME_DNS_NOT_FOUND]: r =>
                    r.httpStatus === 422 &&
                    r?.payload?.code === 'SPUCNR-V0-F0' &&
                    matchCustomDomainMessage(r, /CNAME record not found/i),
                [errorTypes.SPONSOR_CNAME_EMPTY]: r =>
                    r.httpStatus === 422 &&
                    r?.payload?.code === 'SPUCNR-V0-F0' &&
                    matchCustomDomainMessage(r, /can not be empty/i),
                [errorTypes.UPDATE_SPONSOR_CNAME_FAILED]: r => r.httpStatus >= 400
            }
        }
    );
}

/**
 * 搬移既有主辦單位自訂網域 CNAME 指向 — promotion-tool
 * Spec: prompt-library/backend/Api/promotion-tool/搬移既有主辦單位自訂網域.yaml
 * ClickUp: https://app.clickup.com/t/86exke5ef
 *
 * 用於舊客戶（已設定 customDomain 但 DNS 仍指向舊 Proxy）補做 Cloudflare for SaaS 流程。
 * 沿用 sponsor 已存的 customDomain，後端重新跑一次 Custom Hostname 建立流程。
 */
export function sponsorCnameMigrateApi(sponsorUuid) {
    return fetches(
        `/services/sponsors/${sponsorUuid}/cname/migrate`,
        feverApiFetchOpts({ method: methods.POST }),
        {
            throwErrConfigs: {
                [errorTypes.SPONSOR_CNAME_MIGRATE_FAILED]: r => r.httpStatus >= 400
            }
        }
    );
}

export function updateSponsorSettingsApi(sponsorId, data) {
    return fetches(
        `/services/sponsors/${sponsorId}/settings`,
        feverApiFetchOpts({ method: methods.PATCH, body: data })
    );
}

export function getAccountRoleInfo(sponsorId) {
    return fetches(`/services/sponsors/${sponsorId}/member/role`, feverApiFetchOpts());
}

export function createInviteCodeApi(sponsorId, roleUuid, turnstileToken) {
    return fetches(
        `/services/sponsors/${sponsorId}/admins/roles/${roleUuid}/invitation`,
        feverApiFetchOpts({ method: methods.POST, turnstileToken })
    );
}

export function sendInviteEmailApi(sponsorId, roleUuid, data, turnstileToken) {
    return fetches(
        `/services/sponsors/${sponsorId}/admins/roles/${roleUuid}/invitation`,
        feverApiFetchOpts({ method: methods.POST, body: data, turnstileToken }),
        {
            throwErrConfigs: {
                [errorTypes.INVITE_EMAIL_ALREADY_EXISTS]: r =>
                    r.httpStatus === 400 &&
                    r?.payload?.code === 'ADMSR-V0-F0' &&
                    r?.payload?.message === 'Already holds the administrator role'
            }
        }
    );
}

export function validateInviteCodeApi(invitationUuid = '') {
    return fetches(
        `/services/admin/invitation/verification?invitationUuid=${invitationUuid}`,
        feverApiFetchOpts()
    );
}

export function bindInviteCodeApi(invitationUuid) {
    return fetches(
        `/services/sponsors/invitation`,
        feverApiFetchOpts({
            method: methods.PATCH,
            body: { invitationUuid }
        })
    );
}

/**
 * 新增 GIDP 帳號 https://app.clickup.com/t/86erynmz1
 * @param {Object} param
 * @param {number} param.sponsorId - 贊助商 ID
 * @param {string} param.roleUuid - 角色 UUID
 * @param {Object} param.body - 新增帳號資料
 * @returns
 */
export function createGidpAccount({ sponsorId, roleUuid, body, turnstileToken }) {
    return fetches(
        `/services/sponsors/${sponsorId}/admins/roles/${roleUuid}/create`,
        feverApiFetchOpts({ method: methods.POST, body, turnstileToken }),
        {
            throwErrConfigs: CREATE_GIDP_ACCOUNT_ERROR
        }
    );
}

/**
 * 更新 GIDP 帳號 https://app.clickup.com/t/86ery7z1a
 * @param {Object} param
 * @param {number} param.sponsorId - 贊助商 ID
 * @param {string} param.adminUuid - 帳號 UUID
 * @param {Object} param.body - 更新帳號資料
 * @returns
 */
export function updateGidpAccount({ sponsorId, adminUuid, body, turnstileToken }) {
    return fetches(
        `/services/sponsors/${sponsorId}/admins/${adminUuid}`,
        feverApiFetchOpts({ method: methods.PATCH, body, turnstileToken }),
        {
            throwErrConfigs: UPDATE_GIDP_ACCOUNT_ERROR
        }
    );
}

/**
 * 取得 GIDP 帳號列表 https://app.clickup.com/t/86erwaqu0
 * @param {Object} param
 * @param {number} param.sponsorId - 贊助商 ID
 * @param {number} param.page - 頁碼
 * @param {number} param.perPage - 每頁筆數
 * @param {string} param.roleType - 角色類型
 * @param {string} param.searchKey - 搜尋關鍵字
 * @returns
 */
export function getAdminsApi({ sponsorId, page = 1, perPage = 15, roleType, searchKey }) {
    const qs = queryString.stringify({
        perPage,
        page,
        search: searchKey || undefined,
        includeType: roleType || undefined
    });
    return fetches(
        `/services/sponsors/${sponsorId}/admins${qs ? `?${qs}` : ''}`,
        feverApiFetchOpts()
    );
}

export function updateAdminRoleApi(sponsorId, adminUuid, roleUuid, turnstileToken) {
    return fetches(
        `/services/sponsors/${sponsorId}/admins/${adminUuid}/roles/${roleUuid}`,
        feverApiFetchOpts({ method: methods.PATCH, turnstileToken })
    );
}

export function deleteAdminApi(sponsorId, adminUuid, turnstileToken) {
    return fetches(
        `/services/sponsors/${sponsorId}/admins/${adminUuid}`,
        feverApiFetchOpts({ method: methods.DELETE, turnstileToken })
    );
}

export function getSecretKey(sponsorId) {
    return fetches(`/services/sponsors/${sponsorId}/secret-key`, feverApiFetchOpts());
}

export function createSecretKey(sponsorId) {
    return fetches(
        `/services/sponsors/${sponsorId}/secret-key`,
        feverApiFetchOpts({ method: methods.PATCH })
    );
}

export function getSponsorAvailPlanItems(sponsorId) {
    return fetches(`/services/sponsors/${sponsorId}/plans/orders/items`, feverApiFetchOpts());
}

export function syncUserOldFbRelatedSponsor() {
    return fetches(`/services/admin/facebook/related`, feverApiFetchOpts({ method: methods.POST }));
}

// NOTE: 當在 partner site 開授權視窗且打的 API 時，授權視窗的 fever-host 這個 header 需要是開啟授權視窗網站的 header
export function getFacebookFanPage({ sponsorId, shortLiveFacebookUserToken }) {
    return fetches(
        `/services/sponsors/${sponsorId}/instagram-token/store-long-live-user-token`,
        feverApiFetchOpts({
            method: methods.POST,
            body: { userToken: shortLiveFacebookUserToken }
        })
    );
}

// NOTE: 用 page id 請後端去跟 FB 取 Page Token 並存在這個 sponsor
export function storeFacebookFanPageToken(sponsorId, fanPageId) {
    return fetches(
        `/services/sponsors/${sponsorId}/instagram-token/store-long-live-page-token`,
        feverApiFetchOpts({
            method: methods.POST,
            body: { pageId: fanPageId }
        })
    );
}

export function subscribeIgWebhook({ sponsorId, campaignId, subscribeMode }) {
    const formData = new FormData();
    formData.append('campaignId', campaignId);
    formData.append('subscribe-mode', subscribeMode);
    return fetches(
        `/services/sponsors/${sponsorId}/ig-webhook/subscription`,
        feverApiFetchOpts({
            method: methods.POST,
            formData
        })
    );
}

// 取得平台資訊
export function getThirdPartyPlatformInfo({ sponsorId }) {
    return fetches(
        `/services/sponsors/${sponsorId}/third-party-platforms/infos`,
        feverApiFetchOpts({ method: methods.GET })
    );
}

// 取得平台設定
export function getThirdPartyPlatformSettings({ sponsorId, platform }) {
    return fetches(
        `/services/sponsors/${sponsorId}/third-party-platforms/settings?platform=${platform}`,
        feverApiFetchOpts({ method: methods.GET })
    );
}

// 寫入平台設定
export function updateThirdPartyPlatformInfo({ sponsorId, platform, settings }) {
    return fetches(
        `/services/sponsors/${sponsorId}/third-party-platforms/settings`,
        feverApiFetchOpts({ method: methods.POST, body: { platform, settings } })
    );
}

// 寫入平台設定
export function bindingECommerceSponsor({ state }) {
    return fetches(
        `/services/ec/binding`,
        feverApiFetchOpts({ method: methods.POST, body: { state } })
    );
}

// 儲存後台授權流程的平台串接 sponsor setting
export function updateECSponsorSetting({ sponsorId, platform, state, feverHost }) {
    return fetches(
        `/services/ec/sponsors/${sponsorId}/settings/${platform}`,
        feverApiFetchOpts({ method: methods.POST, body: { state }, feverHost })
    );
}
